/**
 * Custom curve (Bezier) editor (W2-05; DESIGN.md §4.12 "Bezier tab", rpe-format.md F§6.7) and the standalone
 * `DIALOG.bezierEditor`.
 *
 *  - Two draggable handles (pointer, or keyboard: focus a handle, arrows move it by 0.01, Shift by 0.1); x is clamped
 *    to [0, 1], y is free (overshoot allowed and shaded); the curve is drawn with Phira's own solver.
 *  - Numeric fields for the four values, a paste box for `cubic-bezier(...)` (pasting anywhere in the editor works
 *    too), "Copy as cubic-bezier", presets (CSS curves and the fitted Quint / Expo In-Out).
 *  - Snap to 0.01 (default on): Phira rounds the handles to 0.01 to share solvers. Dragging, arrow keys, pasted
 *    text and typed field values all snap while it is on; with it off they stay exact (an arrow key moves a
 *    coordinate by exactly its step, 0.255 -> 0.265).
 *  - Collision warning: another curve of the chart rounding to the same f32 cache key with different handles makes
 *    Phira draw one of them with the other's handles; the warning says which.
 *  - The dialog: Enter applies (Ctrl+Enter also from a field being typed in), Esc cancels (applyKeys.ts).
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { DialogProps } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { Ease } from '../../core/ease/index';
import type { BezierPoints } from '../../core/ease/types';
import type { KeyId, LineId } from '../../core/model/types';
import { Button, Checkbox, Dialog, IconButton, NumberField } from '../../ui';
import {
  bezierCollisions,
  bezierPresets,
  clampX,
  formatCubicBezier,
  formatHandles,
  normalizeHandles,
  nudgeHandle,
  parseCubicBezier,
  snapHandle,
  type CollisionReport,
} from './bezierModel';
import { useApplyKeys } from './applyKeys';
import { CurvePreview } from './CurvePreview';
import { m } from './easing.messages';
import s from './Easing.module.css';
import { useDocVersion, useServicesStore, useText } from './hooks';
import { createLivePreview, createProgressRelay, type LivePreview } from './livePreview';
import { formatValue, segmentPreview } from './segmentPreview';
import { useReducedMotion } from '../../ui/internal/useReducedMotion';

const SIZE = 200;
/** Visible y range of the canvas (handles may go beyond and are then drawn at the edge). */
const Y_MIN = -0.6;
const Y_MAX = 1.6;
const PAD = 14;

export interface BezierEditorProps {
  readonly value: BezierPoints;
  onChange(p: BezierPoints, final: boolean): void;
  readonly collision: CollisionReport | null;
  readonly lineName: (line: LineId) => string;
  readonly snap: boolean;
  onSnapChange(on: boolean): void;
  /** Feedback for paste / copy (toasts). */
  onNotice?(text: string, kind: 'info' | 'warning'): void;
}

function px(x: number): number {
  return PAD + x * (SIZE - 2 * PAD);
}
function py(y: number): number {
  return PAD + ((Y_MAX - y) / (Y_MAX - Y_MIN)) * (SIZE - 2 * PAD);
}
function fromPx(clientX: number, clientY: number, rect: DOMRect): readonly [number, number] {
  const sx = rect.width > 0 ? SIZE / rect.width : 1;
  const sy = rect.height > 0 ? SIZE / rect.height : 1;
  const lx = (clientX - rect.left) * sx;
  const ly = (clientY - rect.top) * sy;
  const x = (lx - PAD) / (SIZE - 2 * PAD);
  const y = Y_MAX - ((ly - PAD) / (SIZE - 2 * PAD)) * (Y_MAX - Y_MIN);
  return [x, y];
}

export function BezierEditor(props: BezierEditorProps) {
  const { t } = useText();
  const { value: p, snap } = props;
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<0 | 1 | null>(null);
  const [paste, setPaste] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);

  const curve = useMemo(() => {
    const pts: string[] = [];
    for (let i = 0; i <= 80; i++) {
      const x = i / 80;
      const y = Ease.bezierY(p, x);
      if (Number.isFinite(y))
        pts.push(`${px(x).toFixed(2)},${py(Math.min(Math.max(y, Y_MIN - 1), Y_MAX + 1)).toFixed(2)}`);
    }
    return pts.join(' ');
  }, [p]);

  const setHandle = (h: 0 | 1, x: number, y: number, final: boolean): void => {
    const next: [number, number, number, number] = [p[0], p[1], p[2], p[3]];
    next[h * 2] = clampX(x);
    next[h * 2 + 1] = y;
    props.onChange(normalizeHandles(next, snap), final);
  };

  const handleKeys = (h: 0 | 1) => (e: KeyboardEvent<SVGElement>) => {
    const step = e.shiftKey ? 0.1 : 0.01;
    let delta: readonly [dx: number, dy: number] | null = null;
    if (e.key === 'ArrowLeft') delta = [-step, 0];
    else if (e.key === 'ArrowRight') delta = [step, 0];
    else if (e.key === 'ArrowUp') delta = [0, step];
    else if (e.key === 'ArrowDown') delta = [0, -step];
    if (delta === null) return;
    e.preventDefault();
    e.stopPropagation();
    const nx: [number, number, number, number] = [p[0], p[1], p[2], p[3]];
    // While snapping is on the nudged handle lands on the 0.01 grid (as a dragged one does); with it off the
    // coordinate moves by exactly the step (0.255 -> 0.265) and the other coordinate stays as it was.
    const [dx, dy] = delta;
    nx[h * 2] = clampX(nudgeHandle(p[h * 2] ?? 0, dx, snap));
    nx[h * 2 + 1] = nudgeHandle(p[h * 2 + 1] ?? 0, dy, snap);
    props.onChange(nx, true);
  };

  const handle = (h: 0 | 1) => {
    const x = p[h * 2] ?? 0;
    const y = p[h * 2 + 1] ?? 0;
    const ax = h === 0 ? 0 : 1;
    const ay = h === 0 ? 0 : 1;
    const cy = Math.min(Math.max(y, Y_MIN), Y_MAX);
    return (
      <g key={h}>
        <line className={s.bezierArm} x1={px(ax)} y1={py(ay)} x2={px(x)} y2={py(cy)} />
        <circle
          className={s.bezierHandle}
          cx={px(x)}
          cy={py(cy)}
          r={7}
          tabIndex={0}
          role="slider"
          aria-label={t(h === 0 ? m.handleStart : m.handleEnd)}
          aria-valuetext={t(m.handleValue, { x: formatValue(x), y: formatValue(y) })}
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={x}
          data-handle={h}
          onKeyDown={handleKeys(h)}
          onPointerDown={(e: ReactPointerEvent<SVGCircleElement>) => {
            e.preventDefault();
            e.stopPropagation();
            drag.current = h;
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e: ReactPointerEvent<SVGCircleElement>) => {
            const rect = svgRef.current?.getBoundingClientRect();
            if (drag.current !== h || rect === undefined) return;
            const [nx, ny] = fromPx(e.clientX, e.clientY, rect);
            setHandle(h, nx, ny, false);
          }}
          onPointerUp={(e: ReactPointerEvent<SVGCircleElement>) => {
            if (drag.current !== h) return;
            drag.current = null;
            e.currentTarget.releasePointerCapture(e.pointerId);
            props.onChange(p, true);
          }}
        />
      </g>
    );
  };

  const applyPasted = (text: string): boolean => {
    const parsed = parseCubicBezier(text);
    if (parsed === null) {
      setPasteError(t(m.pasteInvalid));
      return false;
    }
    setPasteError(null);
    const fixed = normalizeHandles(parsed, false);
    if (fixed[0] !== parsed[0] || fixed[2] !== parsed[2]) props.onNotice?.(t(m.pasteXRange), 'warning');
    props.onChange(snap ? normalizeHandles(fixed, true) : fixed, true);
    props.onNotice?.(t(m.pasted, { value: formatCubicBezier(fixed) }), 'info');
    return true;
  };

  const field = (i: 0 | 1 | 2 | 3, label: string) => (
    <NumberField
      label={label}
      stacked
      value={{ kind: 'value', value: p[i] }}
      step={0.01}
      precision={3}
      {...(i % 2 === 0 ? { min: 0, max: 1 } : {})}
      onCommit={(v) => {
        if (v.kind !== 'number') return;
        const next: [number, number, number, number] = [p[0], p[1], p[2], p[3]];
        // A typed value snaps like a dragged, nudged or pasted one (only the field typed in changes).
        const typed = snap ? snapHandle(v.value) : v.value;
        next[i] = i % 2 === 0 ? clampX(typed) : typed;
        props.onChange(next, true);
      }}
    />
  );

  const overshoots = Ease.overshoots({ kind: 'bezier', p });
  const col = props.collision;
  return (
    <div
      className={s.bezier}
      onPaste={(e) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
        const text = e.clipboardData.getData('text');
        if (parseCubicBezier(text) !== null) {
          e.preventDefault();
          applyPasted(text);
        }
      }}
    >
      <div className={s.bezierMain}>
        <svg
          ref={svgRef}
          className={s.bezierCanvas}
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${String(SIZE)} ${String(SIZE)}`}
          role="group"
          aria-label={t(m.bezierCanvas)}
        >
          <rect
            className={s.bezierOvershoot}
            x={px(0)}
            y={py(Y_MAX)}
            width={px(1) - px(0)}
            height={py(1) - py(Y_MAX)}
          />
          <rect className={s.bezierOvershoot} x={px(0)} y={py(0)} width={px(1) - px(0)} height={py(Y_MIN) - py(0)} />
          <rect className={s.bezierFrame} x={px(0)} y={py(1)} width={px(1) - px(0)} height={py(0) - py(1)} />
          <polyline className={s.bezierCurve} points={curve} />
          {handle(0)}
          {handle(1)}
        </svg>
        <div className={s.bezierSide}>
          <div className={s.bezierFields}>
            {field(0, t(m.x1))}
            {field(1, t(m.y1))}
            {field(2, t(m.x2))}
            {field(3, t(m.y2))}
          </div>
          <Checkbox
            label={t(m.snap)}
            checked={snap}
            onChange={(on) => {
              props.onSnapChange(on);
            }}
          />
          <div className={s.pasteRow}>
            <label className={s.pasteLabel}>
              <span>{t(m.pasteLabel)}</span>
              <input
                className={s.input}
                value={paste}
                placeholder={t(m.pastePlaceholder)}
                spellCheck={false}
                onChange={(e) => {
                  setPaste(e.target.value);
                  setPasteError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (applyPasted(paste)) setPaste('');
                  }
                }}
              />
            </label>
            <Button
              size="sm"
              onClick={() => {
                if (applyPasted(paste)) setPaste('');
              }}
            >
              {t(m.pasteApply)}
            </Button>
            <IconButton
              icon="Copy"
              size="sm"
              label={{ key: m.copyCss }}
              onClick={() => {
                const text = formatCubicBezier(p);
                void navigator.clipboard.writeText(text).then(
                  () => props.onNotice?.(t(m.copiedCss, { value: text }), 'info'),
                  () => props.onNotice?.(text, 'info'),
                );
              }}
            />
          </div>
          {pasteError !== null && (
            <p className={s.error} role="alert">
              {pasteError}
            </p>
          )}
          {overshoots && <p className={s.note}>{t(m.overshootNote)}</p>}
        </div>
      </div>
      <div className={s.presets} role="group" aria-label={t(m.presetsLabel)}>
        <span className={s.presetsTitle}>{t(m.presets)}</span>
        {bezierPresets().map((preset) => (
          <Button
            key={preset.id}
            size="sm"
            variant="ghost"
            onClick={() => {
              props.onChange(preset.p, true);
            }}
          >
            {t(`easing.preset.${preset.id}`)}
          </Button>
        ))}
      </div>
      {col !== null && col.collisions.length > 0 && (
        <div className={s.warning} role="status">
          <strong>{t(m.collisionTitle, { count: col.collisions.length })}</strong>
          <p>
            {t(m.collisionOthers, {
              count: col.collisions.length,
              lines: [...new Set(col.collisions.map((c) => props.lineName(c.line)))].join(', '),
            })}
          </p>
          <p>
            {col.drawnWith !== null && col.drawnWith !== p
              ? t(m.collisionDrawnWith, { points: formatHandles(col.drawnWith) })
              : t(m.collisionOwnFirst)}
          </p>
          <p>{t(m.collisionFix)}</p>
        </div>
      )}
    </div>
  );
}

/** Collision report of a curve meant for `segments` in the open project (null without a project). */
export function collisionsIn(
  store: ReturnType<typeof useServicesStore>,
  p: BezierPoints,
  segments: readonly KeyId[],
): CollisionReport | null {
  return store === null ? null : bezierCollisions(store.doc, store.idx, p, segments);
}

/** `DIALOG.bezierEditor`: edit a custom curve with a live preview; resolves with the handles (the caller applies). */
export function BezierEditorDialog(props: DialogProps<'bezierEditor'>) {
  const { services } = props;
  const { t } = useText();
  const store = useServicesStore(services);
  useDocVersion(store);
  const reduced = useReducedMotion();
  const [p, setP] = useState<BezierPoints>(props.props.initial);
  const [snap, setSnap] = useState(true);
  const segments = props.props.segments;
  const live = useRef<LivePreview | null>(null);
  const [progress] = useState(createProgressRelay);
  const first = segments[0];
  const info = store === null || first === undefined ? null : segmentPreview(store.doc, store.idx, first);
  const [original] = useState(() =>
    store === null || first === undefined ? null : (store.doc.keys.get(first)?.ease ?? null),
  );
  const collision = collisionsIn(store, p, segments);

  useEffect(() => {
    const preview = createLivePreview(services, {
      meta: { label: { key: m.txBezier } },
      loop: !reduced,
      onLoopChange: progress.notify,
    });
    live.current = preview;
    progress.attach(preview);
    return () => {
      preview.end();
      live.current = null;
      progress.attach(null);
    };
  }, [services, reduced, progress]);

  const t0 = info?.span.t0 ?? null;
  const t1 = info?.span.t1 ?? null;
  useEffect(() => {
    const preview = live.current;
    if (preview === null || segments.length === 0) return;
    const span = t0 === null || t1 === null ? null : { t0, t1 };
    preview.show((tx) => Core.keys.setEase(tx, { segments, ease: { kind: 'bezier', p } }), span);
  }, [p, segments, t0, t1]);

  const finish = (result: BezierPoints | null): void => {
    live.current?.end();
    props.close(result);
  };
  // Enter applies (Ctrl+Enter from any field), Esc cancels (the Dialog).
  const keys = useApplyKeys(() => {
    finish(p);
  }, false);

  const lineName = (line: LineId): string => store?.doc.lines.get(line)?.name ?? String(line);
  return (
    <Dialog
      open
      title={t(m.bezierTitle)}
      size="lg"
      onClose={() => {
        finish(null);
      }}
      footer={
        <div className={s.keysScope} {...keys}>
          <Button
            variant="ghost"
            onClick={() => {
              finish(null);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              finish(p);
            }}
          >
            {t(m.bezierApply)}
          </Button>
        </div>
      }
    >
      <div className={s.bezierDialog} {...keys}>
        <BezierEditor
          value={p}
          onChange={(next) => {
            setP(next);
          }}
          collision={collision}
          lineName={lineName}
          snap={snap}
          onSnapChange={setSnap}
          onNotice={(text, kind) => {
            services.ui.toast({ key: m.notice, params: { text } }, { kind });
          }}
        />
        <div className={s.previewBox}>
          <span className={s.previewTitle}>{t(m.preview)}</span>
          <CurvePreview
            ease={{ kind: 'bezier', p }}
            compare={original}
            {...(info?.from !== null && info?.from !== undefined && info.to !== null
              ? { from: info.from, to: info.to }
              : {})}
            formatValue={formatValue}
            label={t(m.plotLabel, { name: formatCubicBezier(p) })}
            progress={progress}
          />
          {info !== null && info.prop !== null && info.from !== null && info.to !== null && (
            <p className={s.note}>
              {t(m.previewOf, {
                prop: info.prop,
                from: formatValue(info.from),
                to: formatValue(info.to),
                beats: formatValue(info.beats),
              })}
            </p>
          )}
        </div>
      </div>
    </Dialog>
  );
}
